// src/pages/finance-portal/trial-balance/TrialBalance.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, Skeleton, Chip,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const TYPE_COLORS = {
    ASSET: '#22c55e',
    LIABILITY: '#ef4444',
    EQUITY: '#8b5cf6',
    REVENUE: '#3b82f6',
    EXPENSE: '#f59e0b',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

export default function TrialBalance() {
    const [data, setData] = useState([]);
    const [totals, setTotals] = useState({ total_debits: 0, total_credits: 0, is_balanced: true });
    const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTrialBalance = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/trial-balance', { params: { as_of_date: asOfDate } });
            if (res.data.success) {
                setData(res.data.data);
                setTotals({
                    total_debits: res.data.total_debits,
                    total_credits: res.data.total_credits,
                    is_balanced: res.data.is_balanced,
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load trial balance');
        } finally {
            setIsLoading(false);
        }
    }, [asOfDate]);

    useEffect(() => { fetchTrialBalance(); }, [fetchTrialBalance]);

    // Group by account type
    const groupedData = {};
    data.forEach(row => {
        const type = row.account_type;
        if (!groupedData[type]) groupedData[type] = [];
        groupedData[type].push(row);
    });

    const typeOrder = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AssessmentIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Trial Balance</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Summary of all account balances</Typography>
                        </Box>
                    </Box>
                    {!isLoading && (
                        <Chip
                            icon={totals.is_balanced ? <CheckCircleIcon /> : <ErrorIcon />}
                            label={totals.is_balanced ? 'Balanced' : 'Unbalanced'}
                            sx={{
                                fontFamily, fontWeight: 600,
                                bgcolor: totals.is_balanced ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.3)',
                                color: '#fff',
                                '& .MuiChip-icon': { color: '#fff' },
                                display: { xs: 'none', md: 'flex' },
                            }}
                        />
                    )}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Date Picker */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <TextField
                        label="As of Date" type="date" size="small"
                        value={asOfDate} onChange={e => setAsOfDate(e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Account No.</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Account Name</TableCell>
                                    <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Debit</TableCell>
                                    <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>Credit</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => <TableRow key={i}><TableCell colSpan={4}><Skeleton height={32} /></TableCell></TableRow>)
                                ) : data.length === 0 ? (
                                    <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No posted journal entries found</Typography>
                                    </TableCell></TableRow>
                                ) : (
                                    typeOrder.filter(t => groupedData[t]).map(type => {
                                        const rows = groupedData[type];
                                        const typeColor = TYPE_COLORS[type] || '#64748b';
                                        const typeDebit = rows.reduce((s, r) => s + r.debit_balance, 0);
                                        const typeCredit = rows.reduce((s, r) => s + r.credit_balance, 0);

                                        return (
                                            <React.Fragment key={type}>
                                                {/* Type Header */}
                                                <TableRow>
                                                    <TableCell colSpan={4} sx={{ bgcolor: `${typeColor}08`, py: 1, borderBottom: `1px solid ${typeColor}20` }}>
                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.8rem', color: typeColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                            {type}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                {/* Rows */}
                                                {rows.map(row => (
                                                    <TableRow key={row.account_id} hover>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', pl: 4 }}>{row.account_number}</TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>{row.account_name}</TableCell>
                                                        <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: row.debit_balance ? '#1e293b' : '#cbd5e1' }}>
                                                            {row.debit_balance ? formatCurrency(row.debit_balance) : '—'}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: row.credit_balance ? '#1e293b' : '#cbd5e1' }}>
                                                            {row.credit_balance ? formatCurrency(row.credit_balance) : '—'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Type Subtotal */}
                                                <TableRow>
                                                    <TableCell colSpan={2} sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: typeColor, textAlign: 'right', pr: 2, borderBottom: `1px solid ${typeColor}20` }}>
                                                        {type} Total
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', borderBottom: `1px solid ${typeColor}20` }}>
                                                        {typeDebit ? formatCurrency(typeDebit) : '—'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', borderBottom: `1px solid ${typeColor}20` }}>
                                                        {typeCredit ? formatCurrency(typeCredit) : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })
                                )}

                                {/* Grand Totals */}
                                {!isLoading && data.length > 0 && (
                                    <TableRow sx={{ bgcolor: totals.is_balanced ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)' }}>
                                        <TableCell colSpan={2} sx={{ fontFamily, fontWeight: 800, fontSize: '0.9rem', color: '#1e293b', textAlign: 'right', pr: 2, borderTop: '2px solid #e2e8f0' }}>
                                            Grand Total
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 800, fontSize: '1rem', color: '#1e293b', borderTop: '2px solid #e2e8f0' }}>
                                            {formatCurrency(totals.total_debits)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 800, fontSize: '1rem', color: '#1e293b', borderTop: '2px solid #e2e8f0' }}>
                                            {formatCurrency(totals.total_credits)}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Box>
        </Box>
    );
}
